"use client";

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import styles from "./ChatAssistantWidget.module.css";

type Sender = "user" | "assistant";

type ChatMessage = {
  id: string;
  sender: Sender;
  text: string;
};

type KnowledgeEntry = {
  id: string;
  title: string;
  keywords: string[];
  response: string;
};

const SUPPORT_CONTACT =
  "Se precisar de ajuda humana, fale com nosso suporte pelos números (19) 99786-1291 ou (11) 91845-6133.";

const normalizeText = (value: string) =>
  value
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[^\p{L}\p{N}\s]/gu, "");

const knowledgeBase: KnowledgeEntry[] = [
  {
    id: "overview",
    title: "Visão geral",
    keywords: [
      "plataforma",
      "funciona",
      "sobre",
      "inicio",
      "home",
      "painel",
      "principal",
      "dashboard",
    ],
    response:
      "A Striva centraliza a operação do seu negócio. O Dashboard reúne indicadores de receita, engajamento, agenda e performance para orientar decisões rápidas assim que você entra na plataforma.",
  },
  {
    id: "schedule",
    title: "Agendamentos",
    keywords: [
      "agenda",
      "agendamento",
      "calendario",
      "horario",
      "servico",
      "compromisso",
      "schedule",
    ],
    response:
      "Em Agenda você visualiza calendários diários e semanais, cria atendimentos, aplica filtros por profissional e confirma presenciais ou on-line. É o melhor caminho para abrir e gerenciar horários com rapidez.",
  },
  {
    id: "subscriptions",
    title: "Vendas de assinaturas",
    keywords: [
      "assinatura",
      "assinaturas",
      "vendas",
      "recorrencia",
      "plano",
      "subscricao",
      "subscription",
    ],
    response:
      "No menu Assinaturas você acompanha planos ativos, métricas de receita recorrente, carteira de assinantes e pode ativar o painel de vendas para disponibilizar ofertas digitais.",
  },
  {
    id: "plans",
    title: "Planos",
    keywords: ["planos", "plano", "pacotes", "ofertas", "pricing"],
    response:
      "Em Assinaturas > Planos você cadastra ou edita planos, definindo periodicidade, valores, benefícios e permissões de venda. Cada plano pode ser publicado imediatamente no link de vendas.",
  },
  {
    id: "bank-account",
    title: "Conta bancária",
    keywords: ["banco", "conta", "recebimento", "financeiro", "bank", "payout"],
    response:
      "Na área Assinaturas > Conta Bancária você abre e acompanha a conta de recebimentos. Basta informar dados do negócio, enviar documentos solicitados e acompanhar o status de análise antes de liberar saques.",
  },

  {
    id: "products",
    title: "Produtos e serviços",
    keywords: ["produto", "servico", "catálogo", "estoque", "produtos"],
    response:
      "Em Produtos você organiza serviços, itens e combos disponíveis para venda, controla disponibilidade por profissional e mantém a comunicação alinhada com o que aparece na agenda e nos planos.",
  },
  {
    id: "register",
    title: "Cadastros",
    keywords: ["cadastro", "clientes", "colaboradores", "equipe", "register"],
    response:
      "No menu Cadastros você mantém clientes, colaboradores e fornecedores organizados. Dá para importar listas, definir papéis e aplicar permissões específicas por usuário.",
  },
  {
    id: "financial",
    title: "Financeiro",
    keywords: ["financeiro", "caixa", "relatorio", "vendas", "pagamentos"],
    response:
      "O módulo Financeiro oferece visão de caixa, relatórios de vendas, comissões, contas a pagar e dashboards analíticos para acompanhar resultados e planejar ações.",
  },
  {
    id: "settings",
    title: "Configurações",
    keywords: ["configuracao", "ajustes", "perfil", "empresa", "parametros"],
    response:
      "Em Configurações você atualiza dados da empresa, preferências operacionais, integrações e controla links públicos. É aqui que você garante que a plataforma reflita o seu modelo de negócio.",
  },
];

const quickActions = knowledgeBase.map(({ id, title }) => ({ id, title }));

const makeId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;

const getAssistantReply = (incoming: string): string => {
  const normalized = normalizeText(incoming);

  const matchedEntry = knowledgeBase.find(({ keywords }) =>
    keywords.some((keyword) => normalized.includes(normalizeText(keyword))),
  );

  if (matchedEntry) {
    return `${matchedEntry.response} ${SUPPORT_CONTACT}`;
  }

  return `Ainda não tenho essa informação aqui. ${SUPPORT_CONTACT}`;
};

const initialMessage: ChatMessage = {
  id: "assistant-intro",
  sender: "assistant",
  text: "Olá! Eu sou o assistente da Striva. Posso explicar como cada área da plataforma funciona, organizar seus agendamentos, mostrar como vender assinaturas e configurar links da agenda. Sobre o que você quer saber?",
};

export default function ChatAssistantWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage]);
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);
  const [showQuickActions, setShowQuickActions] = useState(true);

  const toggleWidget = () => {
    setIsOpen((prev) => !prev);
  };

  const appendConversation = (userText: string, assistantBasis?: string) => {
    const trimmedUserText = userText.trim();
    if (!trimmedUserText) return;

    const assistantInput = assistantBasis?.trim();
    const assistantText = getAssistantReply(
      assistantInput && assistantInput.length > 0
        ? assistantInput
        : trimmedUserText,
    );

    const userMessage: ChatMessage = {
      id: makeId("user"),
      sender: "user",
      text: trimmedUserText,
    };

    const assistantMessage: ChatMessage = {
      id: makeId("assistant"),
      sender: "assistant",
      text: assistantText,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
  };

  const handleSendMessage = (text: string) => {
    appendConversation(text);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSendMessage(draft);
    setDraft("");
  };

  const handleQuickAction = (id: string) => {
    const entry = knowledgeBase.find((item) => item.id === id);
    if (!entry) return;
    appendConversation(entry.title, entry.keywords.join(" "));
    setShowQuickActions(false);
  };

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setShowQuickActions(true);
    }
  }, [isOpen]);

  const showWidget = useMemo(
    () => ({
      container: `${styles.container} ${isOpen ? styles.open : ""}`.trim(),
    }),
    [isOpen],
  );

  return (
    <div className={showWidget.container} style={{ zIndex: 999 }}>
      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.header}>
            <div>
              <strong>Assistente Striva</strong>
              <p>Pronto para explicar qualquer área da plataforma.</p>
            </div>
            <button
              type="button"
              aria-label="Fechar assistente"
              className={styles.closeButton}
              onClick={toggleWidget}
            >
              <X size={18} />
            </button>
          </div>

          <div className={styles.quickActions}>
            {showQuickActions ? (
              quickActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className={styles.quickAction}
                  onClick={() => handleQuickAction(action.id)}
                >
                  {action.title}
                </button>
              ))
            ) : (
              <button
                type="button"
                className={`${styles.quickAction} ${styles.quickActionToggle}`.trim()}
                onClick={() => setShowQuickActions(true)}
              >
                Escolher outro assunto
              </button>
            )}
          </div>

          <div className={styles.messages} ref={listRef}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={
                  message.sender === "assistant"
                    ? styles.assistantMessage
                    : styles.userMessage
                }
              >
                <span>{message.text}</span>
              </div>
            ))}
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Digite sua dúvida"
              aria-label="Mensagem"
            />
            <button type="submit">Enviar</button>
          </form>
        </div>
      )}

      <button
        type="button"
        className={styles.fab}
        aria-label={isOpen ? "Minimizar assistente" : "Abrir assistente"}
        onClick={toggleWidget}
      >
        <MessageCircle size={22} />
      </button>
    </div>
  );
}
